import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { views } from "../../Configs/config";
import { useSelector } from "react-redux";
import TopbarFilters from "../../Components/TopbarFilters";
import LeftSection from "../../Components/LeftSection";
import RightSection from "../../Components/RightSection";
import styled from "styled-components";
import { viewSizeCalculator, FILTER_CONFIG } from "../../utils";
import HospitalComparison from "../../Components/HospitalComparison";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  FILTER_KEYS,
  DEFAULT_GLOBAL_FILTER,
} from "../../Configs/filters.config";
import SortingModelEnhanced from "../../Components/SortingModel/SortingModelEnhanced.jsx";
import { useFilters } from "../../hooks/useFilters";
import { useResolvedStates } from "../../hooks/useResolvedStates";
import { useQuery } from "@tanstack/react-query";
import {
  getHospitalSummary,
  getCardFilterCounts,
} from "../../services/apiService";

const HomePage = ({
  toggleState,
  resetFlag,
  setResetFlag,
}) => {
  const [sortFilters, setSortFilters] = useState("RANKING");
  // Local draft state for the advanced-filter form. The applied filters live in
  // the URL; this only holds in-progress form input.
  const [Datafiltered, setDatafiltered] = useState({});
  const { tableViewData, countByProvidernumber } = useSelector(
    (store) => store.app,
  );
  const {
    partnerExclusiveFlag,
    globalFilter,
    filters,
    quickFilter,
    search,
  } = useFilters();
  // Legend filters no longer keep their states in the URL; resolve the active
  // selection (explicit or legend-derived) so card counts stay accurate.
  const { effectiveStates: states } = useResolvedStates();
  // Visibility moved to Redux: sortingModel.isVisible
  const [showSkelton, setShowSkelton] = useState(true);
  const [totalHospitals, setTotalHospitals] = useState(0);
  const location = useLocation();
  const [globalCustomFilterOption, setGlobalCustomFilterOption] =
    useState(globalFilter);

  // Hospital count summary (per global-filter bucket). Effectively static, so
  // it is cached and shared across remounts via react-query.
  const { data: summaryData } = useQuery({
    queryKey: ["hospital-summary"],
    queryFn: ({ signal }) => getHospitalSummary(signal),
    staleTime: 30 * 60 * 1000,
  });
  const hospitalStats = useMemo(
    () =>
      summaryData?.status === "success" && summaryData?.summary
        ? summaryData.summary
        : {},
    [summaryData],
  );

  // Build the card-filter-counts request body from the current filter context.
  // It doubles as the react-query key, so identical filter combinations reuse
  // the cached response instead of refetching (replaces the manual abort/refetch
  // effect this component used to run).
  // Scope precedence matches the API: states > search_term > provider_numbers.
  const filterCountsBody = useMemo(() => {
    const body = {
      partnerExclusiveArea: partnerExclusiveFlag,
      globalFilter: globalFilter?.value,
    };
    const providerNumbers =
      countByProvidernumber && countByProvidernumber.length > 0
        ? countByProvidernumber
        : null;
    const hasAdvanced = filters && Object.keys(filters).length > 0;
    const selectedStates = (states || []).filter((i) => i !== "");

    if (selectedStates.length) {
      body.states = selectedStates;
    } else if (search) {
      // Text search: count over the full match set (not first-page IDs).
      body.search_term = search;
    } else if (providerNumbers) {
      // Bookmarks / legacy provider-number scope.
      body.provider_numbers = providerNumbers;
    } else if (location.pathname === "/" && !hasAdvanced) {
      // National default on home with no scope — omit states.
    }

    if (hasAdvanced) {
      body.filters = filters;
      if (filters.state) body.states = [filters.state];
    }
    return body;
  }, [
    partnerExclusiveFlag,
    globalFilter,
    states,
    filters,
    search,
    countByProvidernumber,
    location.pathname,
  ]);

  const { data: filterCountsData } = useQuery({
    queryKey: ["card-filter-counts", filterCountsBody],
    queryFn: ({ signal }) => getCardFilterCounts(filterCountsBody, signal),
  });
  const filterCounts = filterCountsData ?? null;

  const getHospitalCountByFilter = useCallback((filterValue, stats) => {
    if (!stats || Object.keys(stats).length === 0) return 0;
    
    switch (filterValue) {
      case 'all':
        return stats.total_hospitals || 0;
      case 'coc_type':
        return stats.cancer_programs || 0;
      case 'aamc':
        return stats.aamc_hospitals || 0;
      case 'non_aamc_community_hospitals':
        return stats.non_aamc_community_hospitals || 0;
      default:
        return stats.total_hospitals || 0;
    }
  }, []);

  // Recompute the headline total whenever the summary or the global filter
  // changes (the summary itself is fetched/cached by the hospital-summary query).
  useEffect(() => {
    if (quickFilter && filterCounts) {
      const count = filterCounts[quickFilter] || 0;
      setTotalHospitals(count);
    } else if (
      filterCounts &&
      typeof filterCounts.total_hospitals === "number"
    ) {
      // card-filter-counts is partner-aware (partnerExclusiveArea) and
      // global-filter-aware, so it reflects the Partner ESA toggle.
      setTotalHospitals(filterCounts.total_hospitals);
    } else if (
      hospitalStats &&
      Object.keys(hospitalStats).length > 0
    ) {
      const count = getHospitalCountByFilter(
        globalFilter?.value || "all",
        hospitalStats
      );

      setTotalHospitals(count);
    }
  }, [
    quickFilter,
    filterCounts,
    globalFilter,
    hospitalStats,
    getHospitalCountByFilter,
  ]);

  const filtersData = useMemo(() => {
    if (!filterCounts) return FILTER_CONFIG;

    return FILTER_CONFIG.map((filter) => ({
      ...filter,
      count: filterCounts[filter.id] || 0,
    }));
  }, [filterCounts]);

  // --- View persisted in the URL (?view=map|table|compare) ------------------
  // The URL `view` param is the single source of truth for which screen is
  // shown — there is no Redux/local mirror to reconcile. Every view change is a
  // single `updateSearchParams({ view })` write, and rendering simply reads the
  // param back. Deriving it on each render means a refresh paints the correct
  // view on the very first frame (no map→table transition flicker).
  const [searchParams] = useSearchParams();
  const urlView = searchParams.get(FILTER_KEYS.VIEW);
  const activeView = [views.MAP, views.TABLE, views.COMPARE].includes(urlView)
    ? urlView
    : views.MAP;

  // The global-filter dropdown mirrors the URL (single source of truth). The
  // map "Reset" button keeps globalFilter in the URL, so the dropdown stays put;
  // the logo reset clears the URL, so globalFilter falls back to the default and
  // the dropdown follows automatically. Custom saved-search selections aren't
  // encoded in the URL, so leave those untouched here (cleared on full reset
  // below).
  useEffect(() => {
    setGlobalCustomFilterOption((prev) =>
      prev?.customSearch ? prev : globalFilter,
    );
  }, [globalFilter]);

  // Logo / global reset: reset the bits of UI that aren't encoded in the URL
  // (sort + any active custom saved-search). Predefined globalFilter is handled
  // by the URL-sync effect above, so we don't touch it here — that's what keeps
  // the map "Reset" button (which shares resetFlag but keeps globalFilter in the
  // URL) from wiping the dropdown.
  useEffect(() => {
    if (resetFlag) {
      setSortFilters("RANKING");
      setGlobalCustomFilterOption((prev) =>
        prev?.customSearch ? DEFAULT_GLOBAL_FILTER : prev,
      );
    }
  }, [resetFlag]);

  return (
    <>
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      {activeView === views.COMPARE ? (
        <Wrapper height={7.7}>
          <HospitalComparison toggleState={toggleState} />
        </Wrapper>
      ) : (
        <>
          <TopbarFilters
            filtersData={filtersData}
            filteredData={Datafiltered}
            setFilteredData={setDatafiltered}
            setSelectedOption={setGlobalCustomFilterOption}
            selectedOption={globalCustomFilterOption}
          />
          <Wrapper view={activeView}>
            {activeView === views.MAP ? (
              <LeftSection
                view={activeView}
                showSkelton={showSkelton}
                setShowSkelton={setShowSkelton}
                selectedOption={sortFilters}
                setSelectedOption={setSortFilters}
                setGlobalCustomFilterOption={setGlobalCustomFilterOption}
                toggleState={toggleState}
                resetFlag={resetFlag}
                setResetFlag={setResetFlag}
                totalHospitals={totalHospitals}
                setTotalHospitals={setTotalHospitals}
                setFilteredData={setDatafiltered}
              />
            ) : (
              <LeftSection
                view={activeView}
                filterFlag={tableViewData?.filterFlag}
                data={tableViewData?.data}
                selectedOption={sortFilters}
                setSelectedOption={setSortFilters}
                setGlobalCustomFilterOption={setGlobalCustomFilterOption}
                toggleState={toggleState}
                resetFlag={resetFlag}
                setResetFlag={setResetFlag}
                totalHospitals={totalHospitals}
                setTotalHospitals={setTotalHospitals}
                setFilteredData={setDatafiltered}
              />
            )}

            <RightSection
              view={activeView}
              toggleState={toggleState}
              totalHospitals={totalHospitals}
              setTotalHospitals={setTotalHospitals}
            />
          </Wrapper>
          <SortingModelEnhanced 
            toggleState={toggleState}
           />
        </>
      )}
    </>
  );
};

export default HomePage;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: ${({ height }) =>
    height
      ? `calc(100% - ${viewSizeCalculator(height)})`
      : `calc(100% - ${viewSizeCalculator(12.4)})`};
  gap: ${({ view }) =>
    view === views.TABLE ? "0" : `${viewSizeCalculator(0.625)}`};
  padding: ${() => `0 ${viewSizeCalculator(1.25)}`};
  transition: gap 400ms ease-in-out;
`;
